/**
 * VideoArea — TV-P3-03
 *
 * Wraps a LiveKit Room in our existing dark-theme chrome. The actual
 * connection params (token + serverUrl) are minted server-side by
 * useLivekitToken (TV-P3-04) so we never embed the LIVEKIT_API_SECRET
 * in the bundle.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────┐
 *   │  GridLayout                                  │
 *   │   ┌─────────┐  ┌─────────┐                   │
 *   │   │ patient │  │ provider│                   │
 *   │   └─────────┘  └─────────┘                   │
 *   ├──────────────────────────────────────────────┤
 *   │  Control bar (mic / camera / share / end…)   │
 *   └──────────────────────────────────────────────┘
 *
 * The control bar is bespoke (preserves the team's design language).
 * Mic + camera buttons read/write the local participant's tracks via
 * `useLocalParticipant`. Screen share + invite get wired up in
 * TV-P3-06; here we still render them but their handlers no-op.
 *
 * Loading / error chrome takes over when there's no token yet so the
 * user never sees a half-mounted LiveKit Room.
 */

import React, { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  useLocalParticipant,
  useRoomContext,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import {
  Mic,
  MicOff,
  Camera,
  CameraOff,
  Share2,
  MessageCircle,
  Phone,
  Pause,
  Square,
  Loader2,
  AlertTriangle,
  Copy,
  Check,
  X,
} from 'lucide-react';
import { firebaseFunctions } from '@/firebase/config';
import { useVisitStore } from '@/stores/visitStore';
import { useAuth } from '@/auth/AuthProvider';
import { useLivekitToken } from '../useLivekitToken';
import { useRoomTimerSync } from '../useRoomTimerSync';

// ── Atoms ─────────────────────────────────────────────────────────────────

function ControlButton({
  icon: Icon,
  label,
  onClick,
  variant = 'default',
  disabled = false,
}) {
  const variants = {
    default: 'bg-slate-700 hover:bg-slate-600 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    success: 'bg-green-600 hover:bg-green-700 text-white',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center gap-1 p-3 rounded-lg transition-all ${variants[variant]} ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      title={label}
    >
      <Icon className="w-5 h-5" />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

// ── In-room renderer (inside <LiveKitRoom>) ───────────────────────────────

function ConferenceTiles() {
  // Pull camera + screen-share tracks. ParticipantTile auto-renders the
  // most relevant source for each tile, so handing it the union gives us
  // an Active Speaker-style grid with screen-share priority later.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  return (
    <GridLayout tracks={tracks} className="h-full">
      <ParticipantTile />
    </GridLayout>
  );
}

function RoomControlBar({ accessToken, roomName }) {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const micEnabled = localParticipant?.isMicrophoneEnabled ?? false;
  const camEnabled = localParticipant?.isCameraEnabled ?? false;
  const screenEnabled = localParticipant?.isScreenShareEnabled ?? false;
  const [inviteOpen, setInviteOpen] = useState(false);

  // TV-P3-05 — RoomEvent → visitStore.pause/resume bridge. Mounted
  // here (inside <LiveKitRoom>) so useRoomContext is available.
  useRoomTimerSync();

  const toggleScreenShare = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setScreenShareEnabled(!screenEnabled);
    } catch (err) {
      // User cancelled the picker, or browser denied — log and move on.
      // eslint-disable-next-line no-console
      console.warn('[VideoArea] screen share toggle failed:', err);
    }
  }, [localParticipant, screenEnabled]);

  // Pause / Resume are wired against the visitStore directly. The
  // auto-pause from useRoomTimerSync handles network drops; this
  // button is for the provider to manually pause for an interruption
  // ("stepping out for a moment") without disconnecting.
  const visitStatus = useVisitStore((s) => s.visitStatus);
  const pauseVisit = useVisitStore((s) => s.pauseVisit);
  const resumeVisit = useVisitStore((s) => s.resumeVisit);
  const isPaused = visitStatus === 'paused';
  const togglePause = useCallback(() => {
    if (isPaused) resumeVisit();
    else pauseVisit();
  }, [isPaused, pauseVisit, resumeVisit]);

  const toggleMic = useCallback(() => {
    if (!localParticipant) return;
    localParticipant.setMicrophoneEnabled(!micEnabled);
  }, [localParticipant, micEnabled]);

  const toggleCam = useCallback(() => {
    if (!localParticipant) return;
    localParticipant.setCameraEnabled(!camEnabled);
  }, [localParticipant, camEnabled]);

  // Screen share + invite + chat land in TV-P3-06. End comes from
  // the parent — LiveVisit owns the FHIR Encounter lifecycle, so it
  // controls the End Visit button.
  return (
    <div className="bg-slate-900 border-t border-slate-800 px-6 py-4">
      <div className="flex items-center justify-center gap-3">
        <ControlButton
          icon={micEnabled ? Mic : MicOff}
          label={micEnabled ? 'Mic On' : 'Mic Off'}
          onClick={toggleMic}
        />
        <ControlButton
          icon={camEnabled ? Camera : CameraOff}
          label={camEnabled ? 'Camera On' : 'Camera Off'}
          onClick={toggleCam}
        />
        <ControlButton
          icon={Share2}
          label={screenEnabled ? 'Sharing' : 'Share'}
          onClick={toggleScreenShare}
          variant={screenEnabled ? 'success' : 'default'}
        />
        <ControlButton
          icon={MessageCircle}
          label="Chat"
          onClick={() => {
            /* TV-P7 — chat panel */
          }}
        />
        <ControlButton
          icon={Phone}
          label="Invite"
          onClick={() => setInviteOpen(true)}
        />
        <div className="w-px h-8 bg-slate-700" />
        <ControlButton
          icon={Pause}
          label={isPaused ? 'Resume' : 'Pause'}
          onClick={togglePause}
          variant={isPaused ? 'success' : 'default'}
        />
        <ControlButton
          icon={Square}
          label="End"
          variant="danger"
          onClick={() => {
            /* parent (LiveVisit) owns end-of-encounter */
          }}
        />
      </div>
      {inviteOpen && (
        <InviteModal
          accessToken={accessToken}
          roomName={roomName}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}

// ── Invite modal (TV-P3-06) ───────────────────────────────────────────────

/**
 * InviteModal — provider mints a guest token via the
 * generateLivekitToken Cloud Function (role=guest, name=<entered>),
 * encodes { token, url, room, name } into the URL hash, and offers
 * a Copy button. The link survives one hour (server TTL) and is
 * scoped to this room only.
 *
 * Hash (not query) so the payload never hits the server access log.
 */
function InviteModal({ accessToken, roomName, onClose }) {
  const [name, setName] = useState('Interpreter');
  const [state, setState] = useState({
    isMinting: false,
    link: null,
    error: null,
  });
  const [copied, setCopied] = useState(false);

  const mint = useCallback(async () => {
    if (!accessToken || !roomName) {
      setState({ isMinting: false, link: null, error: new Error('Missing token or room') });
      return;
    }
    if (!firebaseFunctions) {
      setState({
        isMinting: false,
        link: null,
        error: new Error('Firebase not initialized'),
      });
      return;
    }
    setState({ isMinting: true, link: null, error: null });
    try {
      const callable = httpsCallable(firebaseFunctions, 'generateLivekitToken');
      const result = await callable({
        keycloakToken: accessToken,
        roomName,
        participantName: name || 'Guest',
        participantRole: 'guest',
      });
      const { token, url } = result?.data || {};
      if (!token || !url) throw new Error('Cloud Function returned no token');
      const payload = btoa(
        JSON.stringify({ token, url, room: roomName, name: name || 'Guest' }),
      )
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      const link = `${window.location.origin}/guest-visit#payload=${payload}`;
      setState({ isMinting: false, link, error: null });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InviteModal] guest mint failed:', err);
      setState({
        isMinting: false,
        link: null,
        error: err instanceof Error ? err : new Error(String(err)),
      });
    }
  }, [accessToken, roomName, name]);

  const copy = useCallback(async () => {
    if (!state.link) return;
    try {
      await navigator.clipboard.writeText(state.link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[InviteModal] clipboard write failed:', err);
    }
  }, [state.link]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Invite guest</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-slate-400 mb-5">
          Generate a one-hour join link for an interpreter or observer.
          They'll land in this room without needing a CollabKare account.
        </p>

        <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
          Guest name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 mb-4 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:border-slate-500"
          placeholder="Interpreter"
        />

        {!state.link && !state.error && (
          <button
            onClick={mint}
            disabled={state.isMinting}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
          >
            {state.isMinting && <Loader2 className="w-4 h-4 animate-spin" />}
            {state.isMinting ? 'Generating…' : 'Generate invite link'}
          </button>
        )}

        {state.error && (
          <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            {state.error.message}
          </div>
        )}

        {state.link && (
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
              Join link (valid 1 hour)
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={state.link}
                className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs font-mono truncate"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={copy}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg flex items-center gap-1"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Wrapping shell ────────────────────────────────────────────────────────

function StatusScreen({ icon, message, sub, tone = 'info' }) {
  const tones = {
    info: 'text-slate-400',
    error: 'text-red-300',
  };
  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 w-full h-full flex flex-col items-center justify-center rounded-lg border border-slate-700">
      <div className={`flex flex-col items-center gap-3 ${tones[tone]}`}>
        {icon}
        <p className="text-sm font-medium">{message}</p>
        {sub && <p className="text-xs text-slate-500 max-w-md text-center">{sub}</p>}
      </div>
    </div>
  );
}

export default function VideoArea({ encounterId }) {
  const { user, token: accessToken } = useAuth();
  const participantName = user?.name || user?.email || 'Participant';

  const {
    token,
    url,
    isLoading,
    error,
  } = useLivekitToken({
    roomName: encounterId,
    participantName,
    // role: omit — server infers from KC bearer
  });

  // Pre-connect chrome: while the token's still being minted, render
  // a clear loader; on auth/server error, surface the message so the
  // provider knows whether to retry vs re-login.
  if (!encounterId) {
    return (
      <div className="flex flex-col h-full bg-slate-950">
        <div className="flex-1 p-4">
          <StatusScreen
            icon={<AlertTriangle className="w-10 h-10" />}
            message="No encounter on the route"
            sub="The /visit/:id route is missing the Encounter id — go back to the queue and Start Visit from there."
            tone="error"
          />
        </div>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-slate-950">
        <div className="flex-1 p-4">
          <StatusScreen
            icon={<Loader2 className="w-10 h-10 animate-spin" />}
            message="Connecting to video room…"
          />
        </div>
      </div>
    );
  }
  if (error || !token || !url) {
    return (
      <div className="flex flex-col h-full bg-slate-950">
        <div className="flex-1 p-4">
          <StatusScreen
            icon={<AlertTriangle className="w-10 h-10" />}
            message="Couldn't connect to the video room"
            sub={error?.message || 'Token mint returned no token. Retry or re-launch the visit.'}
            tone="error"
          />
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={token}
      serverUrl={url}
      connect
      audio
      video
      data-lk-theme="default"
      className="flex flex-col h-full bg-slate-950"
    >
      <div className="flex-1 p-4 overflow-hidden">
        <ConferenceTiles />
      </div>
      <RoomAudioRenderer />
      <RoomControlBar accessToken={accessToken} roomName={encounterId} />
    </LiveKitRoom>
  );
}
